<script setup lang="ts">
import { computed, defineEmits, onBeforeUnmount, onMounted, useSlots } from "vue"

const { title, size, modalId } = defineProps({
  title: String,
  size: { type: String, default: "md" },
  modalId: { type: String, required: true },
})

const emit = defineEmits(["shown", "hidden"])

const sizeClass = computed(() => `modal-${size}`)

const slots = useSlots()

// detect if footer slot has meaningful content
const hasFooter = computed(() => {
  const f = slots.footer && slots.footer()
  if (!f || f.length === 0) return false
  return f.some((node) => {
    // text node with non-whitespace or any element
    if (typeof node.children === "string") {
      return node.children.trim() !== ""
    }
    return true
  })
})

onMounted(() => {
  const modalEl = document.getElementById(modalId)
  if (!modalEl) return

  const handleShown = () => emit("shown")
  const handleHidden = () => emit("hidden")

  modalEl.addEventListener("shown.bs.modal", handleShown)
  modalEl.addEventListener("hidden.bs.modal", handleHidden)

  onBeforeUnmount(() => {
    modalEl.removeEventListener("shown.bs.modal", handleShown)
    modalEl.removeEventListener("hidden.bs.modal", handleHidden)
  })
})
</script>

<template>
  <div class="modal fade" :id="modalId" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog" :class="sizeClass">
      <div class="modal-content">
        <div class="modal-header" v-if="title">
          <h1 class="modal-title fs-5">{{ title }}</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <slot />
        </div>
        <div class="modal-footer" v-if="hasFooter">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-body ::v-deep(img),
.modal-body ::v-deep(video) {
  width: 100%;
}
</style>
